import { computed, reactive, ref, type Ref } from 'vue';

import type { useUserStore } from '@/stores/user';

import AnalyticsHelper from '@/helpers/analytics';

import type {
  SlideOverCampaignCriteriaItem,
  SlideOverReactivationForm,
} from '../types';

type UserStore = ReturnType<typeof useUserStore>;

export function useSlideOverReactivation(params: {
  campaignId: Ref<number | null | undefined>;
  campaignCriteria: Ref<SlideOverCampaignCriteriaItem[] | undefined>;
  buildInitialReactivationCriteria: () => Record<string, string | string[]>;
  userStore: UserStore;
  onSuccess: () => void;
}) {
  const form = reactive<SlideOverReactivationForm>({
    criteria: {},
  });

  const hasAllCriteriaSelected = computed(() => {
    const criteriaItems = params.campaignCriteria.value;
    if (!criteriaItems?.length) {
      return true;
    }

    for (const item of criteriaItems) {
      const value = form.criteria[item.criteriaId];
      if (
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length < 1)
      ) {
        return false;
      }
    }

    return true;
  });

  const isSubmitting = ref(false);

  const initializeReactivationCriteria = () => {
    form.criteria = params.buildInitialReactivationCriteria();
  };

  const submitReactivation = async () => {
    const campaignId = params.campaignId.value;
    if (!campaignId || isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;

    try {
      const userCriteria = Object.entries(form.criteria)
        .map(([criteriaId, answer]) => ({
          criteriaId: parseInt(criteriaId, 10),
          answerKeys: Array.isArray(answer) ? answer : undefined,
          answerKey: Array.isArray(answer) ? undefined : answer,
        }))
        .filter(
          (criteria) =>
            (criteria.answerKeys && criteria.answerKeys.length > 0) ||
            (criteria.answerKey && criteria.answerKey !== '')
        );

      await params.userStore.joinCampaign({
        campaignId,
        userCriteria,
      });

      AnalyticsHelper.trackEvent('hs_reactivate_confirmation', {
        user_properties: 'helper',
      });

      params.onSuccess();
    } finally {
      isSubmitting.value = false;
    }
  };

  const resetReactivation = () => {
    form.criteria = {};
  };

  return {
    reactivationForm: form,
    hasAllCriteriaSelected,
    isReactivationSubmitting: isSubmitting,
    initializeReactivationCriteria,
    submitReactivation,
    resetReactivation,
  };
}
